import React, { useState, useEffect, useMemo } from "react";
import { Table, Button, Modal, Form, Input, Select, Switch, Tag, Space, message } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_DOMAIN } from "../../constants";
import { useAuth } from "../../contexts/AuthContext";

export function RulesPanel() {
  const { authState } = useAuth();
  const userId = authState?.id;
  const head = useMemo(() => ({ headers: { accessToken: localStorage.getItem("accessToken") } }), []);
  const [rules, setRules] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const levelMap = useMemo(() => {
    const m = {};
    levels.forEach(l => { m[l.priority] = l; });
    return m;
  }, [levels]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rulesRes, levelsRes] = await Promise.all([
        axios.get(`${API_DOMAIN}/approval-rules`, head),
        axios.get(`${API_DOMAIN}/approval-levels`, head),
      ]);
      setRules(rulesRes.data?.data ?? rulesRes.data ?? []);
      setLevels(levelsRes.data?.data ?? levelsRes.data ?? []);
    } catch { message.error("Failed to load rules"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      action_key: row.action_key,
      action_label: row.action_label,
      required_priority: row.required_priority,
      allow_override: !!row.allow_override,
      is_active: !!row.is_active,
    });
    setModalOpen(true);
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ allow_override: true, is_active: true });
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const body = {
        ...values,
        allow_override: values.allow_override ? 1 : 0,
        is_active: values.is_active ? 1 : 0,
        userId,
      };
      if (editing) {
        await axios.patch(`${API_DOMAIN}/approval-rules/${editing.id}`, body, head);
        message.success("Rule updated");
      } else {
        await axios.post(`${API_DOMAIN}/approval-rules`, body, head);
        message.success("Rule created");
      }
      setModalOpen(false);
      fetchAll();
    } catch (e) {
      message.error(e?.response?.data?.message || "Save failed");
    } finally { setSaving(false); }
  };

  const columns = [
    { title: "Action", dataIndex: "action_label", key: "action_label", render: v => <strong>{v}</strong> },
    { title: "Key", dataIndex: "action_key", key: "action_key", render: v => <Tag>{v}</Tag> },
    {
      title: "Required Level", dataIndex: "required_priority", key: "required_priority",
      render: v => {
        const level = levelMap[v];
        return level
          ? <Tag color="purple">{level.name} (P{v})</Tag>
          : <Tag color="default">Priority {v}</Tag>;
      },
    },
    {
      title: "Override Allowed", dataIndex: "allow_override", key: "allow_override",
      render: v => <Tag color={v ? "orange" : "default"}>{v ? "Yes" : "No"}</Tag>,
    },
    {
      title: "Status", dataIndex: "is_active", key: "is_active",
      render: v => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>,
    },
    {
      title: "Actions", key: "actions",
      render: (_, row) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>Edit</Button>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Rule</Button>
      </div>
      <Table
        dataSource={rules}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={false}
      />
      <Modal
        open={modalOpen}
        title={editing ? "Edit Approval Rule" : "New Approval Rule"}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="action_key" label="Action Key" rules={[{ required: true }]}
            extra="Unique identifier used in code, e.g. quote_send">
            <Input placeholder="e.g. discount_approval" disabled={!!editing} />
          </Form.Item>
          <Form.Item name="action_label" label="Action Label" rules={[{ required: true }]}>
            <Input placeholder="e.g. Approve Discount" />
          </Form.Item>
          <Form.Item name="required_priority" label="Required Approval Level" rules={[{ required: true }]}>
            <Select placeholder="Select level">
              {levels.filter(l => l.is_active).map(l => (
                <Select.Option key={l.id} value={l.priority}>
                  {l.name} (Priority {l.priority})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="allow_override" label="Allow Override (Escalate)" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
